import Layer from 'ol/layer/Layer';
import BaseLayer from 'ol/layer/Layer';
import { Source } from 'ol/source';
import Group from 'ol/layer/Group';

/**
 * Determines wheter the layer is potential displayable (independent of the visibility setting) in the given resolution.
 * @param layer The layer.
 * @param viewResolution The actual map view resolution
 */
export const isDisplayable = (layer?: Layer<Source> | Group, viewResolution?: number) =>
  layer && viewResolution ? layer.getMinResolution() <= viewResolution && viewResolution <= layer.getMaxResolution() : false;

/**
 * The visibility of a layer can have two states -> "true", "false"
 * The visibility of a group layer (groups multiple layer together can have three states -> "true", "false", "indeterminate" )
 */
export enum visible {
  true,
  false,
  indeterminate,
}

/**
 * Gets the visibility of a Layer or a Group as a whole.
 * @param layer The layer whose visibility will be returned.
 * @returns The visibility for a Layer -> "true" or "false" for a Group -> "true", "false" or "indeterminate".
 */
export const isVisible = (layer: Layer<Source> | Group | undefined): visible => {
  if (layer instanceof Layer) {
    return layer.getVisible() ? visible.true : visible.false;
  } else if (layer instanceof Group) {
    let layers: Layer<Source>[] = []; // stores the child layers
    getRecursiveLayers(layer, layers);
    return areLayersVisible(layers);
  } else {
    return visible.false;
  }
};

/**
 * Sets the visibility of a Layer or a Group as a whole.
 * @param isVisible The visiblilty to set.
 * @param layer The layer whose visibility will be returned.
 * @returns The visibility for a Layer -> "true" or "false" for a Group -> "true", "false" or "indeterminate".
 */
export const setVisible = (isVisible: boolean, layer: BaseLayer<Source> | Group | undefined): void => {
  if (layer instanceof Layer) {
    layer.setVisible(isVisible);
  } else if (layer instanceof Group) {
    layer.getLayers().forEach(groupLayer => {
      if (groupLayer instanceof Layer || groupLayer instanceof Group) {
        setVisible(isVisible, groupLayer);
      }
    });
  }
};

/**
 *
 * @param layer Gets recursive all layers of a group layer.
 * @param layers The variable that holds the result.
 */
const getRecursiveLayers = (layer: Group, layers: Layer<Source>[]) => {
  layer.getLayersArray().forEach(layer => {
    if (layer instanceof Group) {
      getRecursiveLayers(layer, layers);
    } else {
      layers.push(layer);
    }
  });
};

export const areLayersVisible = (layers: Layer<Source>[]) => {
  let visibleCount = 0;
  layers.forEach(layer => {
    if (layer.getVisible() === true) {
      visibleCount += 1;
    }
  });
  if (visibleCount === 0) {
    return visible.false;
  } else if (layers.length === visibleCount) {
    return visible.true;
  } else {
    return visible.indeterminate;
  }
};

export const toggleVisibility = (layer: Layer<Source> | Group) => {
  let visibility = isVisible(layer);
  if (visibility === visible.true || visibility === visible.indeterminate) {
    visibility = visible.false;
  } else {
    visibility = visible.true;
  }
  setVisible(visibility === visible.true, layer);
  return visibility;
};
